import React from 'react';
import {DatePicker, InputAdornment} from '@deere/fuel-react';
import {CalendarMonth} from '@mui/icons-material';

interface SessionDatePickerProps {
    sessionKey: string;
    startDate: any;
    endDate: any;
    minDate: string;
    startDateLabel: React.ReactNode;
    endDateLabel: React.ReactNode;
    startDateHelper: string;
    selectStartDatePlaceholder: string;
    selectEndDatePlaceholder: string;
    onStartDateChange: (date: Date) => void;
    onEndDateChange: (date: Date | null) => void;
    selectedCDICourseHours: string | null;
}

const maxHoursPerDay = 8;

const saturdayDayIndex = 6;
const sundayDayIndex = 0;

const parseCourseHours = (courseHours: string | null): number => {
    if (!courseHours) {
        return 0;
    }

    const hours = parseFloat(courseHours);

    return isNaN(hours) ? 0 : hours;
};

const getRequiredCourseDays = (courseHours: number): number => {
    if (courseHours <= 0) {
        return 1;
    }

    return Math.max(1, Math.ceil(courseHours / maxHoursPerDay));
};

const isWeekend = (date: Date): boolean => {
    const day = date.getDay();

    return day === sundayDayIndex || day === saturdayDayIndex;
};

const advanceToNextWeekday = (date: Date): Date => {
    const nextDate = new Date(date);

    nextDate.setDate(nextDate.getDate() + 1);

    return isWeekend(nextDate) ? advanceToNextWeekday(nextDate) : nextDate;
};

const getLastClassDate = (startDate: Date, totalDays: number): Date => {
    let endDate = new Date(startDate);

    for (let currentDay = 1; currentDay < totalDays; currentDay += 1) {
        endDate = advanceToNextWeekday(endDate);
    }

    return endDate;
};

const SessionDatePicker: React.FC<SessionDatePickerProps> = ({
    sessionKey,
    startDate,
    endDate,
    minDate,
    startDateLabel,
    endDateLabel,
    startDateHelper,
    selectStartDatePlaceholder,
    selectEndDatePlaceholder,
    onStartDateChange,
    onEndDateChange,
    selectedCDICourseHours
}) => {
    const toDate = (date: any): any => {
        if (!date) {
            return '';
        }
        return new Date(date);
    };

    const calculateMinEndDate = (start: any, courseHours: string | null): string => {
        if (!start) {
            return '';
        }

        const hours = parseCourseHours(courseHours);

        if (hours <= 0) {
            return '';
        }

        const totalDays = getRequiredCourseDays(hours);
        const lastClassDate = getLastClassDate(new Date(start), totalDays);

        return lastClassDate.toLocaleDateString('en-CA');
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
            }}
        >
            <div>
                <DatePicker
                    InputProps={{
                        id: `${sessionKey}-startDate`,
                        helperText: startDateHelper,
                        label: startDateLabel,
                        startAdornment: <InputAdornment position='start'><CalendarMonth/></InputAdornment>
                    }}
                    flatpickrOptions={{
                        minDate,
                        disable: [
                            (date: Date): any => isWeekend(date)
                        ]
                    }}
                    id={`${sessionKey}-startDate`}
                    onChange={(dates: Date[]): void => {
                        if (dates.length > 0) {
                            onStartDateChange(dates[0]);
                        }
                    }}
                    placeholder={selectStartDatePlaceholder}
                    value={toDate(startDate)}
                />
            </div>
            <div>
                <DatePicker
                    InputProps={{
                        id: `${sessionKey}-endDate`,
                        label: endDateLabel,
                        startAdornment: <InputAdornment position='start'><CalendarMonth/></InputAdornment>
                    }}
                    disabled={!startDate}
                    flatpickrOptions={{
                        minDate: startDate ?
                            calculateMinEndDate(startDate, selectedCDICourseHours) :
                            minDate,
                        disable: [
                            (date: Date): boolean => isWeekend(date)
                        ]
                    }}
                    id={`${sessionKey}-endDate`}
                    key={startDate ? new Date(startDate).toLocaleDateString('en-CA') : 'no-start'}
                    onChange={(dates: Date[]): void => {
                        if (dates.length > 0) {
                            onEndDateChange(dates[0]);
                        }
                    }}
                    placeholder={selectEndDatePlaceholder}
                    value={toDate(endDate)}
                />
            </div>
        </div>
    );
};

export default SessionDatePicker;
